import React, { useContext, useEffect, useCallback } from "react";
import styled from "styled-components";
import { FOCUS_BORDER } from "../../config/focusStyles";
import { FiltersContext } from "./filter.context";
import { useCategoryList } from "../../utils/hooks/useCategoryList";
import { useGenreList } from "../../utils/hooks/useGenreList";
import { contentTypeFilters, sortingFilters } from "../../config/enums";
import FocusableSelectBoundary from "../common/focusableSelectBoundary";
import { useCountryList } from "../../utils/hooks/useCountryList";
import { CloseRounded } from "@mui/icons-material";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import {
  Box,
  ButtonBase,
} from "@mui/material";
import { useInputMode } from "../../utils/hooks/useInputMode";

export function CategoryFilter({ setCategory, category }) {
  const [selected, setSelected] = React.useState();

  const { items, isLoading } = useCategoryList();

  useEffect(() => {
    setSelected(category);
  }, [category]);

  return (
    <FocusableSelectBoundary
      instanceFocusKey={"category-filter"}
      label={"دسته بندی"}
      options={items}
      onChange={(val) => {
        setCategory(val);
        setSelected(val);
      }}
      value={selected}
      mode="single"
      isLoading={isLoading}
    />
  );
}

export function GenreFilter({ setGenre, genre }) {
  const [selected, setSelected] = React.useState();
  const { isLoading, items } = useGenreList();

  useEffect(() => {
    setSelected(genre);
  }, [genre]);

  return (
    <FocusableSelectBoundary
      instanceFocusKey={"genre-filter"}
      label={"ژانر"}
      options={items}
      onChange={(val) => {
        setGenre(val);
        setSelected(val);
      }}
      isLoading={isLoading}
      value={selected}
      mode="single"
    />
  );
}

export function CountryFilter() {
  const { setCountry } = useContext(FiltersContext);
  const [selected, setSelected] = React.useState();
  const { items, isLoading } = useCountryList();

  return (
    <FocusableSelectBoundary
      instanceFocusKey={"country-filter"}
      label={"کشور سازنده"}
      options={items}
      onChange={(val) => {
        setCountry(val);
        setSelected(val);
      }}
      value={selected}
      mode="single"
      isLoading={isLoading}
    />
  );
}

export const SortingTypeFilter = () => {
  const { setSortingType } = useContext(FiltersContext);
  const [selected, setSelected] = React.useState();

  return (
    <FocusableSelectBoundary
      instanceFocusKey={"sortingType-filter"}
      label={"ترتیب نمایش"}
      options={sortingFilters}
      onChange={(val) => {
        setSortingType(val);
        setSelected(val);
      }}
      value={selected}
      mode="single"
    />
  );
};

export const ContentTypeFilter = ({ setKind, kind }) => {
  const [selected, setSelected] = React.useState();

  useEffect(() => {
    setSelected(kind);
  }, [kind]);

  return (
    <FocusableSelectBoundary
      instanceFocusKey={"contentType-filter"}
      label={"نوع محتوا"}
      options={contentTypeFilters}
      onChange={(val) => {
        setKind(val);
        setSelected(val);
      }}
      mode="single"
      value={selected}
    />
  );
};

export const ResetFiltersButton = ({ resetFilters }) => {
  const { focusKey, ref, focused, focusSelf } = useFocusable({
    onEnterPress: resetFilters,
    focusKey: "reset-filter-btn",
  });
  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  return (
    <FocusContext.Provider value={focusKey}>
      <ButtonBase
        ref={ref}
        onClick={resetFilters}
        onMouseEnter={handleMouseEnter}
        size="large"
        isIconOnly
        style={{
          border: focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive,
          backgroundColor: "#333",
          borderRadius: "50%",
          padding: 10,
          margin: "0 1rem",
        }}
      >
        <CloseRounded fontSize={"large"} htmlColor="#bfbfbf" />
      </ButtonBase>
    </FocusContext.Provider>
  );
};

const SearchButtonContainer = styled.div`
  position: relative;
  min-width: fit-content;
  cursor: pointer;
  margin-right: auto;
`;

const SearchButtonBase = styled.button`
  min-width: fit-content;
  background: #8a1108;
  font-size: 32px;
  font-weight: 500;
  color: #fff;
  border-radius: 17px;
  padding: 0.5rem 1rem;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  height:72px;
  &:hover {
    background-color: rgb(80, 0, 0);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SearchButtonBorderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: ${props => props.$focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive};
  border-radius: 17px;
  pointer-events: none;
  z-index: 10;
`;

const SearchButtonText = styled.span`
  min-width: fit-content;
  font-size: 28px;
  font-weight: 700;
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  opacity: 0.5;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const SearchButton = ({
  onEnterPress = () => { },
  onFocus = () => { },
  loading = false,
}) => {
  const { ref, focused, focusSelf } = useFocusable({
    onEnterPress,
    onFocus,
  });
  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  return (
    <SearchButtonContainer ref={ref} onClick={onEnterPress} onMouseEnter={handleMouseEnter}>
      <SearchButtonBase disabled={loading}>
        <div className="min-w-[200px] flex justify-center items-center mx-auto">
          {loading ? (
            <LoadingSpinner />
          ) : (
            <SearchButtonText>جستجو</SearchButtonText>
          )}
        </div>
      </SearchButtonBase>
      <SearchButtonBorderOverlay $focused={focused} />
    </SearchButtonContainer>
  );
};

const SearchFilters = ({
  onSearch,
  resetFilters,
  mountReset,
  setFilters,
  filters,
}) => {
  const { ref, focusKey } = useFocusable({
    focusKey: "search-filters-parent",
  });

  const setCategory = (category) => {
    setFilters((prev) => ({ ...prev, category }));
  };

  const setGenre = (genre) => {
    setFilters((prev) => ({ ...prev, genre }));
  };

  const setKind = (kind) => {
    setFilters((prev) => ({ ...prev, kind }));
  };

  return (
    <FocusContext.Provider value={focusKey}>
      <Box display={"flex"} width={"100%"} alignItems={"flex-start"} ref={ref} height={100} overflow={"visible"}>
        <ContentTypeFilter setKind={setKind} kind={filters.kind} />
        <Box px={1} />
        <CategoryFilter setCategory={setCategory} category={filters.category} />
        <Box px={1} />
        <GenreFilter setGenre={setGenre} genre={filters.genre} />
        <Box
          display={"flex"}
          flexDirection={"row"}
          minWidth={500}
          justifyContent={"flex-end"}
          mt={'16px'}
        >
          {mountReset && <ResetFiltersButton resetFilters={resetFilters} />}
          <SearchButton onEnterPress={onSearch} />
        </Box>
      </Box>
    </FocusContext.Provider>
  );
};

export default SearchFilters;
